<script lang="ts" setup>
import { SfLoaderCircular, SfProgressCircular, useDisclosure } from '@storefront-ui/vue';
import { useSdk } from '~/sdk';
import { Button } from '~/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import usePricesData from '@/composables/usePricesData';
import { useAddToCartText } from '~/composables/useAddToCart';
import PreorderConfirmationModal from '~/components/PreorderConfirmationModal.vue';
import BackorderConfirmationModal from '~/components/BackorderConfirmationModal.vue';

const props = defineProps<{
  title?: string;
  prestashopProductId?: string | number;
}>();

interface VariantGroup {
  group_name: string;
  name: string;
  group_type: string;
  default: number;
  options: { id_attribute: string; name: string }[];
}

// Each block instance gets its own cache key so two blocks showing the same product
// (or the same product elsewhere on the page) don't share fetched data or variant selection.
const instanceId = useId();

// Locally-scoped variant selection — intentionally not the page's route-query-based
// `useProduct()`/`VProductVariant`, which is a global singleton keyed off the URL and would
// leak a `variant` query param onto the host page or collide across multiple block instances.
const activeVariant = ref<{ groupId: string; attributeId: string } | null>(null);

const { data, pending, refresh: fetchProduct } = await useAsyncData(
  `topproduct-${instanceId}-${props.prestashopProductId}`,
  () => {
    if (!props.prestashopProductId) return Promise.resolve(null);
    return useSdk().commerce.getProduct({
      id: String(props.prestashopProductId),
      groupId: activeVariant.value?.groupId ?? null,
      attributeId: activeVariant.value?.attributeId ?? null,
    });
  },
);

const product = computed(() => data.value ?? null);
const { data: pricesData } = usePricesData(product);
const { text: addToCartText } = useAddToCartText(product);
const { quantityInCart, quantitySelectable, isAvailable } = useCartItem(product);

const { isOpen, open } = useDisclosure({ initialValue: false });
const selectedQuantity = ref(1);

const groups = computed((): Record<string, VariantGroup> => product.value?.groups ?? {});
const sizeStatuses = computed((): Record<string, string> => product.value?.sizeStatuses ?? {});
const selectedVariants = ref<Record<string, string>>({});
const isUnavailableStatus = (status?: string) => {
  const normalized = status?.toLowerCase();
  return normalized === 'unavailable' || normalized === 'outofstock';
};

// Seed each group's selection with its default option as soon as the data arrives,
// without overwriting a choice the user already made for a group.
watch(groups, (newGroups) => {
  for (const groupKey in newGroups) {
    const group = newGroups[groupKey];
    if (group && selectedVariants.value[groupKey] === undefined) {
      selectedVariants.value[groupKey] = String(group.default);
    }
  }
}, { immediate: true });

async function selectVariant(groupKey: string, attributeId: string) {
  selectedVariants.value[groupKey] = attributeId;
  activeVariant.value = { groupId: groupKey, attributeId };
  await fetchProduct();
}

// Mirrors useAddToCartText: only an unselectable single variant (no group, non-default
// attribute, not a preorder) needs the full product page; everything else - configurable
// products (add their default combo) and preorders - is handled directly in the widget.
const requiresVariantSelection = computed(() => {
  if (product.value?.variants?.length) return false;
  if (product.value?.id_product_attribute === '0') return false;
  if (isPreorder.value) return false;
  return true;
});

const {
  loading,
  showPreorderModal,
  showBackorderModal,
  isPreorder,
  doAddToCart,
  confirmPreorder,
  confirmBackorder,
} = useProductAddToCart(product, selectedQuantity, {
  beforeAddToCart: async () => {
    if (!requiresVariantSelection.value) return false;
    await navigateTo(product.value!.product_url, { external: true });
    return true;
  },
  afterAddToCart: fetchProduct,
});

const isButtonDisabled = computed(() => {
  if (loading.value || pending.value) return true;
  if (requiresVariantSelection.value) return false;
  return !isAvailable.value;
});

const availabilityMessage = useProductAvailabilityMessage(product, { emptyWhenNoProduct: true });
</script>

<template>
  <VContainer class="py-12">
    <VSection :title="title">
      <div v-if="pending && !product" class="flex justify-center py-12">
        <SfProgressCircular size="lg" />
      </div>

      <Card v-else-if="product" class="overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Left: product gallery -->
        <section class="md:h-full">
            <VGallery
                :images="(product.images as any)"
                :is-open="isOpen"
                :product="(product as any)"
                @show-gallery="open"
                class="bg-white"
            />
        </section>

          <!-- Right: product info -->
          <CardContent class="flex flex-col gap-6 p-6 lg:p-10">

            <!-- Brand + SKU -->
            <div class="flex flex-row flex-wrap divide-x divide-gray-300 text-sm">
              <div v-if="product.manufacturer_name" class="flex items-center gap-1 pr-3">
                <span class="font-normal">{{ $t('Brand') }}:</span>
                <NuxtLink
                  :to="(product.manufacturer as any)?.url"
                  class="font-bold text-primary hover:text-primary-700 hover:underline hover:underline-offset-2 transition-colors"
                >
                  {{ product.manufacturer_name }}
                </NuxtLink>
              </div>
              <div v-if="product.mpn" class="flex items-center gap-1 pl-3">
                <span class="font-bold">SKU:</span>
                <span class="font-semibold">{{ product.mpn }}</span>
              </div>
            </div>

            <!-- Name -->
            <NuxtLink :to="product.product_url" class="hover:text-primary transition-colors">
              <h2 class="font-bold text-2xl">{{ product.name }}</h2>
            </NuxtLink>

            <!-- Short description -->
            <div v-if="product.description_short" class="prose text-sm" v-html="product.description_short" />

            <!-- Price -->
            <VProductPagePrice :prices-data="(pricesData as any)" />

            <!-- Delivery info -->
            <div v-if="product.delivery_information" class="text-sm text-gray-600">
              {{ product.delivery_information }}
            </div>

            <!-- Variant selection -->
            <div v-if="Object.keys(groups).length > 0" class="flex flex-col gap-3">
              <div class="flex flex-col gap-3" v-for="(group, groupKey) in groups" :key="groupKey">
                <Label class="font-semibold text-base">{{ group.name }}</Label>
                <ToggleGroup
                  v-model="selectedVariants[groupKey]"
                  type="single"
                  variant="outline"
                  class="justify-start flex-wrap"
                  @update:model-value="
                    (value) => {
                      if (typeof value === 'string' && value) selectVariant(String(groupKey), value);
                    }
                  "
                >
                  <ToggleGroupItem
                    v-for="attribute in group.options"
                    :key="`${attribute.id_attribute}`"
                    :value="`${attribute.id_attribute}`"
                    class="relative border min-w-12 font-bold text-base py-2 px-3"
                    :disabled="pending"
                    :class="{
                      'bg-gray-200 text-gray-400 line-through': isUnavailableStatus(sizeStatuses[String(attribute.id_attribute)]),
                    }"
                  >
                    {{ attribute.name }}
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>

            <!-- Availability status -->
            <div v-if="!pending && availabilityMessage" class="font-semibold" :class="[isAvailable ? 'text-green-600' : 'text-orange-600']">
              {{ availabilityMessage }}
            </div>
            <div v-else-if="pending">
              <SfLoaderCircular size="sm" />
            </div>

            <!-- Cart rules -->
            <div v-if="product.cart_rules?.length" class="flex flex-col gap-2">
              <div v-for="rule in product.cart_rules" :key="(rule as any).id">
                <VCartRule :rule="rule" />
              </div>
            </div>

            <!-- Attachments -->
            <div v-if="product.attachments.length"  class="flex items-center gap-4 divide-x divide-gray-300">
                <div class="flex items-center gap-2">
                    <svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                        d="M7.22222 7.22222V12.5556M7.22222 12.5556L4.55556 9.88889M7.22222 12.5556L9.88889 9.88889M11.6667 17H2.77778C1.79594 17 1 16.2041 1 15.2222V2.77778C1 1.79594 1.79594 1 2.77778 1H7.74292C7.97867 1 8.20476 1.09365 8.37146 1.26035L13.1841 6.07298C13.3508 6.23968 13.4444 6.46578 13.4444 6.70152V15.2222C13.4444 16.2041 12.6485 17 11.6667 17Z"
                        stroke="#374151"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        />
                    </svg>

                    <a
                        :href="`https://shop-ps.pogliani.com/index.php?controller=attachment&id_attachment=${product.attachments[0].id_attachment}`"
                        class="text-base text-primary font-bold underline hover:text-primary-700 transition-colors"
                        target="_blank"
                        download
                        rel="noopener noreferrer"
                        >{{ $t('products.manual') }}</a
                    >
                </div>
            </div>

            <hr />

            <div class="flex flex-col gap-3">
                <!-- Quantity + Add to cart -->
                <div class="flex flex-wrap gap-3">
                    <QuantitySelector
                        class="h-11 min-w-30!"
                        v-model="selectedQuantity"
                        :min="1"
                        :max="quantitySelectable"
                    />
                    <Button :disabled="isButtonDisabled" @click="doAddToCart" class="h-11">
                        <SfProgressCircular v-show="loading" class="mr-2!" size="sm" />
                        {{ addToCartText }}
                    </Button>

                    <!-- Product Availability Notification -->
                    <ProductAvailabilityNotification
                        v-if="!isAvailable"
                        :product-id="product.id_product"
                        :product-attribute-id="product.id_product_attribute"
                        :product-name="product.name"
                    />
                </div>

                <!-- Cart status message -->
                <div v-if="quantityInCart > 0 && !isAvailable" class="space-y-2">
                    <div class="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md text-sm">
                        {{ $t(`products.last_cart_item_text`) }}
                    </div>
                    <NuxtLink
                        v-if="quantityInCart > 0"
                        :to="$localePath('/cart')"
                        class="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 text-sm font-medium underline underline-offset-2 transition-colors"
                    >
                        {{ $t('products.view_cart') }}
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </NuxtLink>
                </div>
            </div>

            <!-- View full product -->
            <NuxtLink :to="product.product_url" class="text-sm text-primary hover:underline font-medium self-start">
              {{ $t('View full product') }} →
            </NuxtLink>

            <CheckoutPaymentIcons class="w-full" />
          </CardContent>
      </Card>
    </VSection>
  </VContainer>

  <!-- Preorder Confirmation Modal -->
  <PreorderConfirmationModal
    v-model:open="showPreorderModal"
    :loading="loading"
    @confirm="confirmPreorder"
  />

  <!-- Backorder Confirmation Modal -->
  <BackorderConfirmationModal
    v-model:open="showBackorderModal"
    :loading="loading"
    :requested-quantity="selectedQuantity"
    :available-quantity="Number(product?.quantity || 0)"
    @confirm="confirmBackorder"
  />
</template>
